/**
 * Extract files from an HTML5 drop event in the most permissive way
 * possible. Handles three sources in order of reliability:
 *
 *   1. event.dataTransfer.files (standard, works 99% of the time)
 *   2. event.dataTransfer.items[].getAsFile() (Outlook on Windows in
 *      "New Outlook" / IRM modes sometimes only populates items)
 *
 * If both return empty, inspects dataTransfer.types to detect the
 * "Outlook gave us a temp file:// URL we can't read" case and returns
 * a structured hint so the caller can show an actionable error.
 *
 * @param {DragEvent} event
 * @returns {{ files: File[], hint?: 'outlook-uri-only' | 'empty' }}
 */
export function extractDroppedFiles(event) {
  // Primary path
  const fromFiles = Array.from(event.dataTransfer?.files || []);
  if (fromFiles.length > 0) {
    return { files: fromFiles };
  }

  // Secondary path: items[].getAsFile()
  const items = event.dataTransfer?.items;
  if (items && items.length > 0) {
    const fromItems = [];
    for (const item of items) {
      if (item.kind === 'file') {
        const f = item.getAsFile();
        if (f) fromItems.push(f);
      }
    }
    if (fromItems.length > 0) {
      return { files: fromItems };
    }
  }

  // No usable files. Inspect dataTransfer.types to see if this looks
  // like an Outlook URL-only drop, so the caller can show a helpful
  // hint instead of a silent fail.
  const types = event.dataTransfer?.types
    ? Array.from(event.dataTransfer.types)
    : [];
  const hasUriList = types.some(
    (t) =>
      t === 'text/uri-list' ||
      t === 'text/x-moz-url' ||
      t === 'application/x-moz-file' ||
      t.toLowerCase().includes('filegroupdescriptor')
  );
  return { files: [], hint: hasUriList ? 'outlook-uri-only' : 'empty' };
}

/**
 * Standard error message for the Outlook-only-URL case. Use this
 * everywhere a drop zone falls back, so the wording is consistent
 * across Jobs, Proposals, Field Reports, etc.
 */
export const OUTLOOK_DROP_HINT =
  "Outlook attachments can't always be dragged directly. " +
  "Save the file to your computer first, then drag it from there.";
